import React, { useState } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { request } from '../../../../util/api';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import { Modal, Button } from '../../../../components';

import SectionContainer from '../SectionContainer';
import css from './SectionLandingHero.module.css';

// Section component that's able to show article content
// The article content is mainly supposed to be inside a block
const SectionLandingHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks: blocksRaw,
    isInsideContainer,
    options,
    onManageDisableScrolling,
    ...otherProps
  } = props;

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sending, setSending] = useState(false);

  const blocks = blocksRaw.filter(b => b.blockName !== 'youtube-block');
  const mediaBlocks = blocksRaw.filter(b => b.blockName == 'youtube-block');

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  const handleOverlayClick = e => {
    // Si el click es en el overlay (no en el modal), cerrar
    if (e.target === e.currentTarget) {
      setIsContactModalOpen(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSuccessMessage('');
    const form = e.target;
    const name = form.name.value;
    const email = form.email.value;
    const message = form.message.value;
    try {
      const res = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message })
      });
      if (res.ok) {
        setSuccessMessage('Your message has been sent!');
        form.reset();
      } else {
        setSuccessMessage('There was an error sending your message.');
      }
    } catch (err) {
      setSuccessMessage('There was an error sending your message.');
    }
    setSending(false);
  };

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(css.landingHero, className)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div>
        {hasHeaderFields ? (
          <header className={classNames(css.heroHeader, defaultClasses.sectionDetails)}>
            <Field data={title} className={classNames(defaultClasses.title, css.landingTitle)} options={fieldOptions} />
            <Field data={description} className={classNames(defaultClasses.description, css.heroDescription)} options={fieldOptions} />
            <div className={css.buttonContainer}>
              <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
              <Button
                className={css.contactButton}
                onClick={() => setIsContactModalOpen(true)}
              >
                Contact Me
              </Button>
            </div>
          </header>
        ) : null}
        {hasBlocks ? (
          <div
            className={classNames(defaultClasses.blockContainer, css.articleMain, css.landingContainer, {
              [css.noSidePaddings]: isInsideContainer,
            })}
          >
            <BlockBuilder
              blocks={blocks}
              sectionId={sectionId}
              ctaButtonClass={defaultClasses.ctaButton}
              options={options}
              {...otherProps}
            />
          </div>
        ) : null}
      </div>
      {mediaBlocks.length ? <div>
        <BlockBuilder
          blocks={mediaBlocks}
          sectionId={sectionId}
          ctaButtonClass={defaultClasses.ctaButton}
          options={options}
          {...otherProps}
        />
      </div> : null}

      <Modal
        id="ContactModal"
        containerClassName={css.modalContainer}
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div style={{ width: '100%', height: '100%' }} onClick={handleOverlayClick}>
          <div className={css.modalContent} style={{ pointerEvents: 'auto' }}>
            <h2>Contact Me</h2>
            <form className={css.contactForm} onSubmit={handleContactSubmit}>
              <div className={css.formField}>
                <label htmlFor="name">Name</label>
                <input type="text" id="name" name="name" required />
              </div>
              <div className={css.formField}>
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" required />
              </div>
              <div className={css.formField}>
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" rows="4" required></textarea>
              </div>
              <Button type="submit" className={css.submitButton} disabled={sending}>
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
            {successMessage && (
              <div style={{ marginTop: '1rem', color: '#388e3c', textAlign: 'center' }}>{successMessage}</div>
            )}
          </div>
        </div>
      </Modal>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionLandingHero.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  isInsideContainer: false,
  options: null,
  onManageDisableScrolling: () => null,
};

SectionLandingHero.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  isInsideContainer: bool,
  options: propTypeOption,
  onManageDisableScrolling: func,
};

export default SectionLandingHero;
